import type { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import type { ConsentUserDto, McpAppDto } from '@lib/dto/mcp.dto'
import { ErrorState } from '@/components/PageState'
import { Button } from '@/components/ui/button'
import { AppIdentity } from './AppIdentity'

interface ConsentLayoutProps {
  app: McpAppDto
  user: ConsentUserDto
  pending: 'approve' | 'deny' | null
  formError?: string | undefined
  /** False when there is nothing to grant (every organization has AI apps turned off). */
  canConnect?: boolean
  onSignOut: () => void
  onApprove: () => void
  onDeny: () => void
  children: ReactNode
}

/** What both consent forms share: which app is asking, who is answering, and Deny / Connect. */
export function ConsentLayout({
  app,
  user,
  pending,
  formError,
  canConnect = true,
  onSignOut,
  onApprove,
  onDeny,
  children,
}: ConsentLayoutProps) {
  return (
    <form
      className="space-y-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        onApprove()
      }}
    >
      <AppIdentity app={app} />
      <p className="text-body">
        Signed in as {user.name} ({user.email}).{' '}
        <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={onSignOut}>
          Not you?
        </Button>
      </p>
      {children}
      {formError ? <ErrorState message={formError} /> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onDeny} disabled={pending !== null}>
          {pending === 'deny' ? <Loader2 className="animate-spin" /> : null}
          Deny
        </Button>
        <Button type="submit" disabled={pending !== null || !canConnect}>
          {pending === 'approve' ? <Loader2 className="animate-spin" /> : null}
          Connect
        </Button>
      </div>
    </form>
  )
}
